import React, { useCallback, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { graphql } from "react-relay";

import { faTimes, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Loader } from "semantic-ui-react";

import type { TeamType, UserType } from "~/common/AssigneeFilter";
import { ReactComponent as SearchIcon } from "~/legacy-assets/images/icons/search.svg";
import useToggle from "~/lib/hooks/useToggle";
import { RunQuery } from "~/lib/relayUtil";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import Popup from "~/ui/legacy/Popup";
import { Input } from "~/ui/legacyFellowIndex";

import type { AssigneeSelectorQuery } from "~/__generated__/AssigneeSelectorQuery.graphql";

import objectiveStyles from "../Objective.module.css";
import styles from "./AssigneeSelector.module.css";

const assigneesQuery = graphql`
	query AssigneeSelectorQuery($query: String) {
		selectUsers: users(first: 15, search: $query, isGuests: false) {
			edges {
				node {
					id
					email
					image
					fullName
					isGuest
				}
			}
		}

		teams(first: 10, query: $query) {
			edges {
				node {
					id
					title
				}
			}
		}
	}
`;

type AssigneeSelectorProps = {
	readOnly?: boolean;
	user: UserType | null;
	team: TeamType | null;
	avatarOnly?: boolean;
	setUser: (u: UserType) => void;
	setTeam: (t: TeamType) => void;
	// This prop should be used to add an OutsideClickHandler to the popup that will
	// Close the dropdown when clicking outside of it. This is used to solve the issue
	// With this dropdown being used inside a Dialog. Both Reach's Dialog and Semantic's Popup uses Portals
	// and, as of today, Popup does not support being rendered inside a specific element.
	// Reach's Dialog stops the propagation on clicks inside the modal content. So we need
	// either to pass a onClick listened to the Dialog and handle it here, or take the easiest
	// route which is to use the outside-click-handler package to handle this.
	// There's probably a better way to solve this but this is sufficient for now.
	forceCloseOnOutsideClick?: boolean;
};

export default function AssigneeSelector({
	user,
	team,
	setUser,
	setTeam,
	readOnly,
	avatarOnly,
	forceCloseOnOutsideClick,
}: AssigneeSelectorProps) {
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [isActive, toggleIsActive] = useToggle(false);
	const [usersList, setUsersList] = useState<UserType[]>([]);
	const [teamsList, setTeamsList] = useState<TeamType[]>([]);
	const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const onQueryLoading = useCallback(() => {
		setLoading(true);
	}, [setLoading]);

	const onQueryResponse = useCallback((result: AssigneeSelectorQuery["response"]) => {
		setLoading(false);
		if (result.selectUsers?.edges) setUsersList(result.selectUsers.edges.map(e => e.node));
		if (result.teams?.edges) setTeamsList(result.teams.edges.map(e => e.node));
	}, []);

	const onClear = useCallback(() => setSearch(""), [setSearch]);

	const onInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(event.target.value);
		},
		[setSearch],
	);

	const selectUser = useCallback(
		(user: UserType) => () => {
			setUser(user);
			toggleIsActive();
		},
		[setUser, toggleIsActive],
	);

	const selectTeam = useCallback(
		(team: TeamType) => () => {
			setTeam(team);
			toggleIsActive();
		},
		[setTeam, toggleIsActive],
	);

	const onFocusSearch = useCallback(() => {
		setIsSearchFocused(true);
	}, []);

	const onBlurSearch = useCallback(() => {
		setIsSearchFocused(false);
	}, []);

	let content = (
		<>
			<RunQuery<AssigneeSelectorQuery>
				query={assigneesQuery}
				variables={{ query: search }}
				fetchPolicy="store-or-network"
				onLoading={onQueryLoading}
				onResult={onQueryResponse}
			/>

			<div className={styles.searchInput}>
				<div className={cx(styles.searchIcon, { loading })}>
					{loading ? (
						<Loader active inline />
					) : (
						<SearchIcon className={cx(styles.searchIcon, { [styles.searchIconDark]: isSearchFocused })} />
					)}
				</div>
				<Input
					innerRef={searchInputRef}
					value={search}
					onChange={onInputChange}
					placeholder="Search users"
					onFocus={onFocusSearch}
					onBlur={onBlurSearch}
					name="assignee_search"
				/>
				{search.length > 0 ? (
					<a href="#" onClick={onClear} className="clear-button">
						<FontAwesomeIcon icon={faTimes} />
					</a>
				) : null}
			</div>
			<div className={styles.searchWrapper}>
				{teamsList.map(t => (
					<div className={styles.searchUserRow} key={`team-${t.id}`} onClick={selectTeam(t)}>
						<Avatar className="image" team={t} link={false} width="24px" uiAvatarOptions={{ textForColor: t.title }} />
						<span>{t.title}</span>
					</div>
				))}
				{usersList.map(u => (
					<div className={styles.searchUserRow} key={`user-${u.id}`} onClick={selectUser(u)}>
						<Avatar className="image" user={u} link={false} width="24px" />
						<span>{u.fullName}</span>
					</div>
				))}
			</div>
		</>
	);

	if (forceCloseOnOutsideClick) {
		content = <OutsideClickHandler onOutsideClick={toggleIsActive}>{content}</OutsideClickHandler>;
	}

	return (
		<Popup
			basic
			open={isActive}
			onOpen={toggleIsActive}
			onClose={toggleIsActive}
			position="bottom center"
			on="click"
			size="small"
			className={styles.assigneePopup}
			trigger={
				<Button
					size="wide"
					className={cx(objectiveStyles.dataButton, { [objectiveStyles.dataButtonDisabled]: readOnly })}
				>
					{team ? (
						<>
							<Avatar link={false} team={team} width="1.5rem" uiAvatarOptions={{ textForColor: team.title }} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>{team.title}</span>}
						</>
					) : user ? (
						<>
							<Avatar link={false} user={user} width="1.5rem" useTitle={false} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>{user.fullName}</span>}
						</>
					) : (
						<>
							<FontAwesomeIcon icon={faUser} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>Assign to...</span>}
						</>
					)}
				</Button>
			}
		>
			{content}
		</Popup>
	);
}
